import { forwardRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, FileCheck2, MapPin } from "lucide-react";
import { caseFilters } from "@/content/caseStudies";
import { ExtLink } from "@/components/common/ExtLink";

export const categoryLabel = (id) => caseFilters.find((f) => f.id === id)?.label ?? "";

export const RegulatoryBadge = () => (
  <span className="inline-flex items-center gap-1.5 text-[12px] text-muted">
    <FileCheck2 className="h-3.5 w-3.5 text-accent" /> Documentation by SPC's in-house regulatory team
  </span>
);

// Case study summary card; `onOpen` shows the full story.
export const CaseCard = forwardRef(({ c, onOpen }, ref) => (
  <motion.article
    ref={ref}
    layout
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    className="group flex h-full flex-col border border-line bg-surface p-6 transition-[border-color,background-color] duration-300 hover:border-accent sm:p-8"
    data-testid={`case-card-${c.id}`}
  >
    <span className="self-start rounded-none bg-accent-soft px-3 py-1 font-mono text-[12px] uppercase tracking-[0.14em] text-accent" data-testid={`case-card-${c.id}-tag`}>
      {categoryLabel(c.category)}
    </span>
    <h3 className="mt-6 font-display font-semibold tracking-[-0.025em] text-[1.6rem] leading-[1.15] text-ink">{c.title}</h3>
    <p className="mt-4 flex items-center gap-2 text-[13px] font-medium text-ink">
      <MapPin className="h-4 w-4 shrink-0 text-accent" /> {c.markets}
    </p>
    <p className="mt-3 text-[15px] leading-relaxed text-muted">{c.summary}</p>
    {c.regulatory && (
      <div className="mt-4">
        <RegulatoryBadge />
      </div>
    )}
    <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-2 pt-7">
      <button
        type="button"
        onClick={() => onOpen(c)}
        className="inline-flex items-center gap-2 text-[14px] font-medium text-accent"
        data-testid={`case-card-${c.id}-open`}
      >
        Read case study
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
      </button>
      {c.link && (
        <ExtLink href={c.link.href} className="text-[13px] !font-normal !text-muted hover:!text-accent" testId={`case-card-${c.id}-link`}>
          {c.link.label}
        </ExtLink>
      )}
    </div>
  </motion.article>
));
CaseCard.displayName = "CaseCard";
